import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useBlocker,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import { updateCustomerProfileFn } from "@modules/account/account.api";
import { customerProfileQueryOptions } from "@modules/account/account.queryOptions";
import { NavigationBlockerDialog } from "@modules/shared/NavigationBlocker/NavigationBlockerDialog";

export const Route = createFileRoute(
  "/$locale/_layout/(protected)/_protected/account/profile",
)({
  component: ProfilePage,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(customerProfileQueryOptions()),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:profile.title"),
          description: t("Seo:profile.description"),
        }),
      ],
    };
  },
});

function ProfilePage() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();
  const router = useRouter();

  const queryClient = useQueryClient();
  const profileOptions = customerProfileQueryOptions();
  const { data } = useSuspenseQuery(profileOptions);
  const customer = data.customer;

  const initial = useMemo(
    () => ({
      firstName: customer?.firstName ?? "",
      lastName: customer?.lastName ?? "",
    }),
    [customer?.firstName, customer?.lastName],
  );

  const [firstName, setFirstName] = useState(initial.firstName);
  const [lastName, setLastName] = useState(initial.lastName);

  // Resync when the underlying query data changes (e.g. after successful save).
  useEffect(() => {
    setFirstName(initial.firstName);
    setLastName(initial.lastName);
  }, [initial.firstName, initial.lastName]);

  const isDirty =
    firstName !== initial.firstName || lastName !== initial.lastName;

  const mutation = useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) =>
      updateCustomerProfileFn({ data: vars }),
    onSuccess: async (res, vars) => {
      const userErrors = res.customerUpdate?.userErrors ?? [];
      if (userErrors.length > 0) {
        toast.error(
          userErrors.map((e) => e.message).join("; ") ||
            t("Account.profile.saveError"),
        );
        return;
      }
      // Seed cache with saved values so `initial` updates immediately and
      // `isDirty` flips to false without waiting for refetch.
      queryClient.setQueryData(profileOptions.queryKey, (old) => {
        if (!old?.customer) return old;
        return {
          ...old,
          customer: {
            ...old.customer,
            firstName: vars.firstName,
            lastName: vars.lastName,
          },
        };
      });
      setFirstName(vars.firstName);
      setLastName(vars.lastName);
      toast.success(t("Account.profile.saved"));
      await router.invalidate();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : t("Account.profile.saveError"),
      );
    },
  });

  // Block in-app navigation while there are unsaved edits. Uses a custom
  // modal (NavigationBlockerDialog) instead of window.confirm.
  const [showBlockerDialog, setShowBlockerDialog] = useState(false);
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty && !mutation.isPending,
    withResolver: true,
    enableBeforeUnload: () => isDirty && !mutation.isPending,
  });

  useEffect(() => {
    if (blocker.status === "blocked") {
      queueMicrotask(() => setShowBlockerDialog(true));
    } else {
      setShowBlockerDialog(false);
    }
  }, [blocker.status]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isDirty || mutation.isPending) return;
    mutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
  };

  const handleCancel = () => {
    setFirstName(initial.firstName);
    setLastName(initial.lastName);
  };

  const inputClass =
    "border-border-5 bg-white text-black focus:border-primary w-full rounded-lg border px-4 py-2 focus:outline-none";
  const readOnlyInputClass =
    "border-border-5 bg-neutral-100 text-neutral-500 w-full rounded-lg border px-4 py-2 cursor-not-allowed";

  return (
    <div className="min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-xl">
        <Link
          to="/$locale/account"
          params={{ locale }}
          className="text-text-3 hover:text-primary text-small mb-4 inline-block"
        >
          ← {t("Account.backToAccount")}
        </Link>

        <h1 className="text-2xlarge text-text-1 mb-8 font-bold">
          {t("Account.profile.title")}
        </h1>

        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          <div>
            <label
              htmlFor="firstName"
              className="text-text-1 mb-2 block font-medium"
            >
              {t("Account.profile.firstName")}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={mutation.isPending}
              autoComplete="given-name"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="text-text-1 mb-2 block font-medium"
            >
              {t("Account.profile.lastName")}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={mutation.isPending}
              autoComplete="family-name"
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="text-text-1 mb-2 block font-medium"
            >
              {t("Account.profile.email")}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={customer?.emailAddress?.emailAddress ?? ""}
              readOnly
              aria-readonly="true"
              className={readOnlyInputClass}
            />
            <p className="text-text-3 text-small mt-1">
              {t("Account.profile.emailReadOnly")}
            </p>
          </div>

          <div>
            <label
              htmlFor="phone"
              className="text-text-1 mb-2 block font-medium"
            >
              {t("Account.profile.phone")}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={customer?.phoneNumber?.phoneNumber ?? ""}
              readOnly
              aria-readonly="true"
              className={readOnlyInputClass}
            />
            <p className="text-text-3 text-small mt-1">
              {t("Account.profile.phoneReadOnly")}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={!isDirty || mutation.isPending}
              className="border-border-5 text-text-1 hover:bg-background-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border px-5 py-2"
            >
              {t("Account.profile.cancel")}
            </button>
            <button
              type="submit"
              disabled={!isDirty || mutation.isPending}
              aria-busy={mutation.isPending}
              className="bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-5 py-2"
            >
              {mutation.isPending
                ? t("Account.profile.saving")
                : t("Account.profile.save")}
            </button>
          </div>
        </form>
      </div>
      {blocker.status === "blocked" ? (
        <NavigationBlockerDialog
          isOpen={showBlockerDialog}
          onConfirm={() => {
            setShowBlockerDialog(false);
            blocker.proceed();
          }}
          onCancel={() => {
            setShowBlockerDialog(false);
            blocker.reset();
          }}
          title={t("Account.profile.unsavedChangesTitle", {
            defaultValue: "Nespremljene promjene",
          })}
          description={t("Account.profile.unsavedChanges")}
        />
      ) : null}
    </div>
  );
}
